import clsx from 'clsx';
import React from 'react';
import { Button as ReactAriaButton } from 'react-aria-components';
import {
  type ArrayPath,
  type Control,
  type FieldValues,
  Path,
  useFieldArray,
  UseFormRegister,
  useFormState,
} from 'react-hook-form';

import { FormFieldLabel } from '#/shared/components/FormField/FormFieldLabel';
import { FormFieldSupportingText } from '#/shared/components/FormField/FormFieldSupportingText';
import { MultiTextInput } from '#/shared/components/FormField/MultiTextInput';
import { MaterialIcon } from '#/shared/components/MaterialIcon';
import { focusRingClassName } from '#/shared/utils/domHelper';

type FormControlMultiTextInputProps<TFieldValues extends FieldValues, TName extends ArrayPath<TFieldValues>> = {
  name: TName;
  control: Control<TFieldValues>;
  minFields?: number;
  maxFields?: number;
  label: string;
  description?: string;
  placeholder?: string;
  type?: 'text';
  className?: string;
  fieldKey?: string; // The key is used to access the value of the field (example: name.index.fieldKey)
  addFieldButtonLabel?: string;
  register: UseFormRegister<TFieldValues>;
  validate?: (value: string | undefined) => string | boolean;
};

export function FormControlMultiTextInput<TFieldValues extends FieldValues, TName extends ArrayPath<TFieldValues>>({
  name,
  control,
  minFields = 1,
  maxFields = 5,
  label,
  description,
  placeholder = 'Enter text',
  type = 'text',
  className,
  fieldKey = 'value',
  addFieldButtonLabel = 'Add another field',
  register,
  validate,
}: FormControlMultiTextInputProps<TFieldValues, TName>) {
  const labelId = React.useId();
  const descriptionId = React.useId();

  const { fields, append, remove } = useFieldArray({
    control,
    name,
  });

  const { errors } = useFormState({ control });

  const addField = () => {
    if (fields.length < maxFields) {
      append({ [fieldKey]: '' } as TFieldValues[TName]);
    }
  };

  const getErrorMessage = React.useCallback(
    (index: number) => {
      const arrayErrors = errors[name];
      if (!arrayErrors || !Array.isArray(arrayErrors) || typeof arrayErrors[index] !== 'object') {
        return undefined;
      }
      return (arrayErrors[index] as Record<string, { message?: string }>)[fieldKey]?.message;
    },
    [errors, name, fieldKey],
  );

  return (
    <div className={className}>
      <FormFieldLabel id={labelId}>{label}</FormFieldLabel>
      {description && (
        <FormFieldSupportingText id={descriptionId} position="above">
          {description}
        </FormFieldSupportingText>
      )}

      {/* Multi input fields */}
      <ul className="space-y-8">
        {fields.map((field, index) => {
          const fieldPath = `${name}.${index}.${fieldKey}` as Path<TFieldValues>;

          const errorMessage = getErrorMessage(index);

          return (
            <li key={field.id}>
              <MultiTextInput
                {...register(fieldPath, { validate })}
                placeholder={placeholder}
                type={type}
                errorMessage={errorMessage}
                errorLeadingIconName="error"
                onRemove={() => remove(index)}
                canRemove={fields.length > minFields}
                aria-labelledby={labelId}
                aria-describedby={description ? descriptionId : undefined}
              />
            </li>
          );
        })}
      </ul>

      {/* Add more fields button */}
      {fields.length < maxFields && (
        <ReactAriaButton
          className={clsx(
            'mt-8 flex items-center gap-4',
            'text-left',
            'text-primary-on-surface-variant text-16 leading-20 font-600',
            'hover:text-primary-on-surface hover:cursor-pointer',
            `focus:text-primary-on-surface ${focusRingClassName}`,
          )}
          onPress={addField}
          aria-label={addFieldButtonLabel}
        >
          <MaterialIcon iconName="add_circle" size="md" />
          {addFieldButtonLabel}
        </ReactAriaButton>
      )}
    </div>
  );
}
